
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { RefreshCw, Trash2, Wand2, Rocket, Search, Info, Trophy } from 'lucide-react';
import { MASTER_THEMES, MASTER_EXTRAS, INTENSITY_OPTIONS } from '../constants.tsx';
import { GenerationSettings, UserEntitlements, UIImage, AuthView } from '../types.ts';
import { generateBrainrotImage, generateBrainrotName, generateBrainrotStory, base64ToBlob, compressMemeImage } from '../services/geminiService.ts';
import { calculateBrainrotRarity } from '../services/rarityService.ts';
import { supabase } from '../lib/supabase.ts';
import { trackEvent } from '../lib/analytics.ts';

// Audio Assets (Short, punchy 1-2s versions)
const SUCCESS_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2019/2019-preview.mp3';
const ROCKET_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/1712/1712-preview.mp3';
const INTERACT_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2574/2574-preview.mp3'; // Crisp Interface Pop
const SURPRISE_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/270/270-preview.mp3'; // Sparkle
const DELETE_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/1113/1113-preview.mp3'; // Digital Cancel
const REFRESH_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/1111/1111-preview.mp3'; // Short Digital Blip

// Silent WAV (0.1s of literal nothing)
const SILENCE_URL = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAP8H';

interface KitchenViewProps {
  user: any;
  isGuest: boolean;
  dailyUsage: number;
  bonusBlasts: number;
  incrementDailyUsage: () => Promise<void>;
  entitlements: UserEntitlements;
  vaultVisibility: string;
  isGenerating: boolean;
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>;
  allHistory: UIImage[];
  setAllHistory: React.Dispatch<React.SetStateAction<UIImage[]>>;
  addAura: (amount: number) => Promise<number>;
  notify: (msg: string, type?: any) => void;
  setPreviewImage: React.Dispatch<React.SetStateAction<UIImage | null>>;
  setActiveView: (view: any) => void;
  setAuthView: (view: AuthView) => void;
}

export default function KitchenView({
  user, isGuest, dailyUsage, bonusBlasts, incrementDailyUsage, entitlements, vaultVisibility,
  isGenerating, setIsGenerating, allHistory, setAllHistory, addAura, notify, setPreviewImage, setActiveView, setAuthView
}: KitchenViewProps) {
  
  const [visibleThemes, setVisibleThemes] = useState(() => [...MASTER_THEMES].sort(() => 0.5 - Math.random()).slice(0, 12));
  const [visibleExtras, setVisibleExtras] = useState(() => [...MASTER_EXTRAS].sort(() => 0.5 - Math.random()).slice(0, 18));
  const [settings, setSettings] = useState<GenerationSettings>({ theme: visibleThemes[0].id, intensity: 'Silly', extras: [] });
  const [currentImage, setCurrentImage] = useState<UIImage | null>(null);
  const [themeSearch, setThemeSearch] = useState('');
  const [modSearch, setModSearch] = useState('');
  const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);
  
  const rocketSoundRef = useRef<HTMLAudioElement | null>(null);
  const successSoundRef = useRef<HTMLAudioElement | null>(null);
  const interactSoundRef = useRef<HTMLAudioElement | null>(null);
  const surpriseSoundRef = useRef<HTMLAudioElement | null>(null);
  const deleteSoundRef = useRef<HTMLAudioElement | null>(null);
  const refreshSoundRef = useRef<HTMLAudioElement | null>(null);
  const silentPrimingRef = useRef<HTMLAudioElement | null>(null);

  // Pre-load audio
  useEffect(() => {
    const setupAudio = (url: string, volume = 0.5) => {
      const audio = new Audio();
      audio.src = url;
      audio.crossOrigin = "anonymous";
      audio.volume = volume;
      audio.preload = 'auto';
      return audio;
    };

    successSoundRef.current = setupAudio(SUCCESS_SOUND_URL);
    interactSoundRef.current = setupAudio(INTERACT_SOUND_URL, 0.4);
    surpriseSoundRef.current = setupAudio(SURPRISE_SOUND_URL, 0.5);
    deleteSoundRef.current = setupAudio(DELETE_SOUND_URL, 0.4);
    refreshSoundRef.current = setupAudio(REFRESH_SOUND_URL, 0.4);
    silentPrimingRef.current = setupAudio(SILENCE_URL, 0); // Total silence
    
    return () => {
      stopRocketSound();
      [successSoundRef, interactSoundRef, surpriseSoundRef, deleteSoundRef, refreshSoundRef, silentPrimingRef].forEach(ref => {
        if (ref.current) {
          ref.current.pause();
          ref.current.src = "";
          ref.current = null;
        }
      });
    };
  }, []);

  const unlockAudio = async () => {
    if (isAudioUnlocked) return;
    // Play the silent file to unlock the speaker
    if (silentPrimingRef.current) {
      try {
        await silentPrimingRef.current.play();
      } catch (e) {}
    }
    setIsAudioUnlocked(true);
  };

  const playSound = (ref: React.RefObject<HTMLAudioElement | null>) => {
    if (ref.current) {
      ref.current.currentTime = 0;
      ref.current.play().catch(() => {
        unlockAudio().then(() => {
          if (ref.current) {
            ref.current.currentTime = 0;
            ref.current.play().catch(() => {});
          }
        });
      });
    }
  };

  const startRocketSound = () => {
    try {
      const audio = new Audio();
      audio.src = ROCKET_SOUND_URL;
      audio.crossOrigin = "anonymous";
      audio.volume = 0.4;
      audio.loop = true;
      audio.play().catch(() => {});
      rocketSoundRef.current = audio;
    } catch (e) {}
  };

  const stopRocketSound = () => {
    if (rocketSoundRef.current) {
      rocketSoundRef.current.pause();
      rocketSoundRef.current.currentTime = 0;
      rocketSoundRef.current = null;
    }
  };

  const handleRefreshThemes = () => {
    unlockAudio();
    playSound(refreshSoundRef);
    setVisibleThemes([...MASTER_THEMES].sort(() => 0.5 - Math.random()).slice(0, 12));
    setThemeSearch('');
  };

  const handleRefreshMods = () => {
    unlockAudio();
    playSound(refreshSoundRef);
    const currentSelectedItems = MASTER_EXTRAS.filter(e => settings.extras.includes(e.name));
    const pool = MASTER_EXTRAS.filter(e => !settings.extras.includes(e.name));
    const randomFill = pool.sort(() => 0.5 - Math.random()).slice(0, 18 - currentSelectedItems.length);
    setVisibleExtras([...currentSelectedItems, ...randomFill]);
    setModSearch('');
  };

  const handleSurpriseMe = () => {
    unlockAudio();
    playSound(surpriseSoundRef);
    const randomThemeObj = MASTER_THEMES[Math.floor(Math.random() * MASTER_THEMES.length)];
    const randomTheme = randomThemeObj.id;
    const randomIntensity = INTENSITY_OPTIONS[Math.floor(Math.random() * INTENSITY_OPTIONS.length)].level;
    const shuffledExtrasPool = [...MASTER_EXTRAS].sort(() => 0.5 - Math.random());
    const chosenExtrasObjects = shuffledExtrasPool.slice(0, Math.floor(Math.random() * 4) + 1);
    const randomExtrasNames = chosenExtrasObjects.map(e => e.name);
    const otherThemes = MASTER_THEMES.filter(t => t.id !== randomTheme);
    const themeFill = otherThemes.sort(() => 0.5 - Math.random()).slice(0, 11);
    setVisibleThemes([randomThemeObj, ...themeFill].sort(() => 0.5 - Math.random()));
    const remainingModsPool = MASTER_EXTRAS.filter(e => !randomExtrasNames.includes(e.name));
    const modsFill = remainingModsPool.sort(() => 0.5 - Math.random()).slice(0, 18 - chosenExtrasObjects.length);
    setVisibleExtras([...chosenExtrasObjects, ...modsFill]);
    setSettings({ theme: randomTheme, intensity: randomIntensity, extras: randomExtrasNames });
    setThemeSearch('');
    setModSearch('');
    notify("CHAOS SELECTED!", "info");
  };

  const handleClear = () => {
    unlockAudio();
    playSound(deleteSoundRef);
    setSettings({ theme: MASTER_THEMES[0].id, intensity: 'Silly', extras: [] });
    setThemeSearch('');
    setModSearch('');
  };

  const handleToggleTheme = (themeId: string) => {
    unlockAudio();
    playSound(interactSoundRef);
    setSettings(s => ({ ...s, theme: themeId }));
  };

  const handleToggleIntensity = (level: string) => {
    unlockAudio();
    playSound(interactSoundRef);
    setSettings(s => ({ ...s, intensity: level as any }));
  };

  const toggleMod = (modName: string) => {
    unlockAudio();
    const isCurrentlySelected = settings.extras.includes(modName);
    if (isCurrentlySelected) {
      playSound(deleteSoundRef);
      setSettings(s => ({ ...s, extras: s.extras.filter(e => e !== modName) }));
    } else {
      if (settings.extras.length >= 6) {
        notify("MAX 6 MODS ALLOWED!", "error");
        return;
      }
      playSound(interactSoundRef);
      setSettings(s => ({ ...s, extras: [...s.extras, modName] }));
    }
  };

  const handleGenerate = async () => {
    if (isGenerating) return;
    unlockAudio(); // SILENT PRIMING

    if (isGuest) {
      notify("IDENTITY REQUIRED TO COOK!", "info");
      setAuthView('signup');
      return;
    }

    const totalCapacity = entitlements.dailyLimit + bonusBlasts;
    if (dailyUsage >= totalCapacity) {
      notify("DAILY LIMIT REACHED! UPGRADE FOR MORE RIZZ.", "error");
      setActiveView('store');
      return;
    }

    setIsGenerating(true);
    setCurrentImage(null);
    startRocketSound();
    
    try {
      const themeObj = MASTER_THEMES.find(t => t.id === settings.theme) || MASTER_THEMES[0];
      const extrasStr = settings.extras.length > 0 ? ` with ${settings.extras.join(', ')}` : '';
      const intensityMod = settings.intensity === 'MAX BRAINROT' || settings.intensity === 'CURSED' 
        ? 'extremely chaotic, distorted, overwhelming, surreal ' 
        : settings.intensity === 'UNCANNY' ? 'unsettling, strange, eerie realism, uncanny valley ' : '';
            
      const fullPrompt = `${intensityMod}${themeObj.promptSnippet}${extrasStr}. style: cinematic digital art, hyper-detailed.`;
      
      const [rawImageUrl, name, story] = await Promise.all([
        generateBrainrotImage(fullPrompt),
        generateBrainrotName(fullPrompt),
        generateBrainrotStory(fullPrompt)
      ]);

      // COMPRESSION: Convert massive PNG to smaller high-quality JPEG
      const compressedUrl = await compressMemeImage(rawImageUrl, 1024, 0.85);
      
      const stats = JSON.parse(localStorage.getItem('brainrot_stats') || '{}');
      const { score, tier, attributes } = calculateBrainrotRarity(stats);

      const newMemeData: any = {
        name, story, prompt: fullPrompt, timestamp: Date.now(),
        user_id: user?.id, rarity: tier, score, attributes,
        is_public: vaultVisibility === 'public', likes_count: 0, dislikes_count: 0
      };

      if (user && !isGuest) {
        const fileName = `${user.id}/${Date.now()}.jpg`;
        const blob = base64ToBlob(compressedUrl);
        await supabase.storage.from('memes').upload(fileName, blob);
        const { data: { publicUrl } } = supabase.storage.from('memes').getPublicUrl(fileName);
        const { data, error: insertError } = await supabase.from('memes').insert({ ...newMemeData, url: publicUrl }).select().single();
        
        if (insertError) {
          // 🛡️ CATCH VAULT SENTINEL ERROR
          if (insertError.message.includes('VAULT_FULL')) {
             notify("VAULT FULL! UPGRADE FOR MORE SLOTS.", "error");
             setActiveView('store');
             // Clean up the storage since the DB record failed
             await supabase.storage.from('memes').remove([fileName]);
             throw new Error("VAULT_FULL");
          }
          throw new Error(insertError.message);
        }

        if (!data) throw new Error("Failed to record cook");
        setCurrentImage(data as any);
        setAllHistory(prev => [data as any, ...(prev || []).filter(Boolean)]);
      } else {
        const localMeme = { ...newMemeData, id: Math.random().toString(), url: compressedUrl };
        setCurrentImage(localMeme as any);
        setAllHistory(prev => [localMeme as any, ...(prev || []).filter(Boolean)]);
      }

      if (successSoundRef.current) {
        successSoundRef.current.currentTime = 0;
        successSoundRef.current.play().catch(() => {});
      }

      addAura(score * 2);
      await incrementDailyUsage();
      
      trackEvent('generate_brainrot', {
        rarity: tier,
        score: score,
        theme: settings.theme,
        intensity: settings.intensity,
        mods_count: settings.extras.length
      });

      notify(`${tier.toUpperCase()} ROT COOKED (Score: ${score})`, "success");
    } catch (err: any) {
      if (err.message !== "VAULT_FULL") {
        notify(err.message || "COOKING FAILED", "error");
      }
    } finally {
      setIsGenerating(false);
      stopRocketSound();
    }
  };

  const getRarityStyles = (rarity?: string) => {
    switch (rarity) {
      case 'uncommon': return 'border-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.3)]';
      case 'rare': return 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.4)]';
      case 'epic': return 'border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.4)]';
      case 'legendary': return 'border-yellow-400 shadow-[0_0_20px_rgba(250,196,21,0.5)]';
      case 'mythic': return 'border-red-500 animate-pulse shadow-[0_0_25px_rgba(239,68,68,0.5)]';
      case 'god': return 'animate-rainbow-border border-white shadow-[0_0_35px_rgba(255,255,255,0.8)] scale-[1.02]';
      default: return 'border-zinc-800';
    }
  };

  const renderModButton = (extra: any) => {
    const isSelected = settings.extras.includes(extra.name);
    return (
      <button 
        key={extra.id} 
        onClick={() => toggleMod(extra.name)} 
        className={`p-2 rounded-xl border-2 transition-all flex flex-col items-center gap-1 group relative ${isSelected ? 'bg-blue-600 border-white shadow-lg scale-105 z-10' : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700 hover:border-zinc-500 hover:text-white'}`}
      >
        <span className="text-2xl">{extra.icon}</span>
        <span className="text-[7px] font-black uppercase tracking-tight text-center truncate w-full">{extra.name}</span>
      </button>
    );
  };

  const recentCooks = useMemo(() => (allHistory || []).filter(Boolean).slice(0, 8), [allHistory]);

  const displayedThemes = useMemo(() => {
    if (!themeSearch.trim()) return visibleThemes;
    return MASTER_THEMES.filter(t => t.name.toLowerCase().includes(themeSearch.toLowerCase())).slice(0, 12);
  }, [themeSearch, visibleThemes]);

  const { selectedExtras, unselectedExtras } = useMemo(() => {
    const searchFiltered = modSearch.trim() ? MASTER_EXTRAS.filter(e => e.name.toLowerCase().includes(modSearch.toLowerCase())) : visibleExtras;
    const selected = searchFiltered.filter(e => settings.extras.includes(e.name));
    const unselected = searchFiltered.filter(e => !settings.extras.includes(e.name));
    return { selectedExtras: selected, unselectedExtras: unselected };
  }, [modSearch, visibleExtras, settings.extras]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in fade-in">
      <h2 className="sr-only">Meme Kitchen - Create Your Brainrot</h2>
      <section className="lg:col-span-5 space-y-6">
        <div className="bg-zinc-900 border-2 border-zinc-800 rounded-3xl p-6 space-y-4 shadow-2xl">
          <div className="flex justify-between items-center">
            <h3 className="font-bungee text-sm text-zinc-400 uppercase">THEMES</h3>
            <div className="flex gap-2">
              <div className="relative group">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600 group-focus-within:text-yellow-400 transition-colors" />
                <input type="text" placeholder="SEARCH..." value={themeSearch} onChange={(e) => setThemeSearch(e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded-lg pl-8 pr-3 py-1 text-[8px] font-bungee text-white outline-none focus:border-yellow-400/50 w-32 transition-all" />
              </div>
              <button onClick={handleRefreshThemes} className="p-1.5 bg-zinc-800 rounded-lg text-yellow-400 hover:scale-110 transition-transform" aria-label="Refresh Themes"><RefreshCw size={14}/></button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {displayedThemes.map(theme => (
              <button key={theme.id} onClick={() => handleToggleTheme(theme.id)} className={`p-3 rounded-2xl border-4 transition-all flex flex-col items-center gap-2 ${settings.theme === theme.id ? `${theme.color} border-white scale-105 shadow-lg` : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700 hover:border-zinc-500 hover:text-white'}`} aria-label={`Select ${theme.name} theme`}>
                <span className="text-3xl">{theme.emoji}</span>
                <span className="text-[8px] font-black uppercase tracking-widest truncate w-full text-center">{theme.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-zinc-900 border-2 border-zinc-800 rounded-3xl p-6 space-y-4 shadow-2xl">
          <div className="flex justify-between items-center">
            <h3 className="font-bungee text-sm text-zinc-400 uppercase">MODS ({settings.extras.length}/6)</h3>
            <div className="flex gap-2">
              <div className="relative group">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600 group-focus-within:text-blue-400 transition-colors" />
                <input type="text" placeholder="SEARCH..." value={modSearch} onChange={(e) => setModSearch(e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded-lg pl-8 pr-3 py-1 text-[8px] font-bungee text-white outline-none focus:border-blue-400/50 w-32 transition-all" />
              </div>
              <button onClick={handleRefreshMods} className="p-1.5 bg-zinc-800 rounded-lg text-blue-400 hover:scale-110 transition-transform" aria-label="Refresh Mods"><RefreshCw size={14}/></button>
              <button onClick={() => { unlockAudio(); playSound(deleteSoundRef); setSettings(s => ({ ...s, extras: [] })); setModSearch(''); }} className="p-1.5 bg-zinc-800 rounded-lg text-zinc-400 hover:text-red-500" aria-label="Clear All Mods"><Trash2 size={14}/></button>
            </div>
          </div>
          <div className="space-y-4">
            {selectedExtras.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-[7px] font-black text-blue-400 uppercase tracking-widest bg-blue-400/10 px-2 py-0.5 rounded border border-blue-400/20">ACTIVE MODS</span>
                </div>
                <div className="grid grid-cols-3 gap-2">{selectedExtras.map(extra => renderModButton(extra))}</div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">{unselectedExtras.map(extra => renderModButton(extra))}</div>
          </div>
        </div>

        <div className="bg-zinc-900 border-2 border-zinc-800 rounded-3xl p-6 space-y-4 shadow-2xl">
          <h3 className="font-bungee text-sm text-zinc-400 uppercase">INTENSITY</h3>
          <div className="grid grid-cols-3 gap-2">
            {INTENSITY_OPTIONS.map(opt => (
              <button key={opt.level} onClick={() => handleToggleIntensity(opt.level)} className={`py-3 rounded-xl font-bungee text-[10px] border-2 transition-all ${settings.intensity === opt.level ? `${opt.color} border-white shadow-md scale-105` : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700 hover:text-white'}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-4">
          <button onClick={handleSurpriseMe} className="flex-1 py-4 bg-zinc-900 border-2 border-indigo-500/30 rounded-2xl font-bungee text-xs uppercase flex items-center justify-center gap-2 hover:bg-zinc-800 transition-all active:scale-95 shadow-lg"><Wand2 size={16} className="text-indigo-400" /> Surprise Me</button>
          <button onClick={handleClear} className="px-6 py-4 bg-zinc-900 border-2 border-zinc-800 rounded-2xl font-bungee text-xs uppercase hover:bg-red-950/20 hover:text-red-500 transition-all shadow-lg">Clear</button>
        </div>

        <button onClick={handleGenerate} disabled={isGenerating} className={`w-full py-8 bg-purple-600 rounded-[2rem] font-bungee text-4xl animate-blast-glow disabled:opacity-50 transition-all shadow-[0_10px_0_0_#7e22ce] active:translate-y-2 active:shadow-none ${isGenerating ? 'animate-cooking pointer-events-none' : ''}`} aria-label="Generate AI Meme">
          {isGenerating ? 'BLASTING...' : "BLAST OFF!"}
        </button>
      </section>

      <section className="lg:col-span-7 space-y-8 flex flex-col">
        {currentImage ? (
          <div className={`bg-zinc-900 border-[6px] rounded-[3.5rem] overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.7)] flex flex-col animate-in zoom-in-95 ${getRarityStyles(currentImage.rarity)}`}>
            <div className="w-full aspect-square overflow-hidden border-b-2 border-white/5">
              <img src={currentImage.url} className="w-full h-full object-contain cursor-pointer hover:scale-105 transition-transform duration-700" onClick={() => setPreviewImage(currentImage)} alt={currentImage.name} />
            </div>
            <div className="p-8 bg-black/60 flex flex-col gap-5">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <h4 className="font-bungee text-4xl text-yellow-400 uppercase leading-none">{currentImage.name}</h4>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest bg-indigo-500/10 px-2 py-0.5 rounded border border-indigo-500/20">{currentImage.rarity}</span>
                    <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">ROT SCORE: {currentImage.score}</span>
                  </div>
                </div>
              </div>
              <p className="text-sm font-black text-zinc-200 italic leading-relaxed border-l-4 border-yellow-400 pl-4 py-1">"{currentImage.story}"</p>
            </div>
          </div>
        ) : (
          <div className="aspect-square rounded-[3rem] border-4 border-dashed border-zinc-800 flex flex-col items-center justify-center text-zinc-700 gap-4 bg-zinc-900/80 shadow-inner">
            <Rocket size={80} className="opacity-10 animate-bounce" />
            <p className="font-bungee text-2xl opacity-10 uppercase tracking-widest text-center px-8">Kitchen is HOT<br/>Forge your destiny</p>
          </div>
        )}
        <div className="flex justify-start items-center gap-4 px-2 mt-2">
          <button onClick={() => setActiveView('rarityguide')} className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border-2 border-zinc-800 rounded-xl font-bungee text-[10px] text-zinc-500 hover:text-yellow-400 hover:border-yellow-400/30 transition-all shadow-lg group"><Info size={14} className="group-hover:rotate-12 transition-transform" /> Rarity Guide</button>
          <button onClick={() => setActiveView('auraguide')} className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border-2 border-zinc-800 rounded-xl font-bungee text-[10px] text-zinc-500 hover:text-indigo-400 hover:border-indigo-400/30 transition-all shadow-lg group"><Trophy size={14} className="group-hover:scale-110 transition-transform" /> Aura Guide</button>
        </div>
        {!isGuest && recentCooks.length > 0 && (
          <div className="mt-8 space-y-4 animate-in slide-in-from-bottom-4">
            <h3 className="font-bungee text-sm text-zinc-500 uppercase tracking-widest border-l-4 border-blue-500 pl-3">RECENT COOKS</h3>
            <div className="grid grid-cols-4 gap-3">
              {recentCooks.map(meme => meme && (
                <div key={meme.id} onClick={() => setPreviewImage(meme)} className={`aspect-square rounded-xl overflow-hidden border-2 cursor-pointer transition-all hover:scale-110 shadow-lg bg-zinc-900 ${getRarityStyles(meme.rarity)}`}>
                  <img src={meme.url} className="w-full h-full object-cover" alt={meme.name || "Meme"} />
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
